/* ==========================================================
   NEVERLAND · 2026 interaction engine
   Vanilla JS. No deps. Lightweight. Prefers-reduced-motion aware.
   ========================================================== */
(function () {
  'use strict';

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isFinePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const lerp = (a, b, t) => a + (b - a) * t;
  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

  /* ─── PAGE LOADER ─── */
  function initLoader() {
    const loader = document.getElementById('nv-loader');
    if (!loader) {
      document.body.classList.add('is-loaded');
      return;
    }
    const done = () => {
      loader.classList.add('is-done');
      document.body.classList.add('is-loaded');
    };
    // Minimum visible duration so animation is felt
    const min = prefersReducedMotion ? 200 : 1000;
    const start = performance.now();
    const finish = () => {
      const elapsed = performance.now() - start;
      setTimeout(done, Math.max(0, min - elapsed));
    };
    if (document.readyState === 'complete') finish();
    else window.addEventListener('load', finish, { once: true });
  }

  /* ─── SCROLL PROGRESS BAR ─── */
  function initProgress() {
    const bar = document.getElementById('nv-progress');
    if (!bar) return;
    const update = () => {
      const h = document.documentElement;
      const max = h.scrollHeight - h.clientHeight;
      const pct = max > 0 ? (h.scrollTop / max) * 100 : 0;
      bar.style.width = pct + '%';
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
  }

  /* ─── CUSTOM CURSOR (dot + ring + label) ─── */
  function initCursor() {
    if (!isFinePointer || prefersReducedMotion) return;
    const cursor = document.getElementById('nv-cursor');
    if (!cursor) return;
    const dot = cursor.querySelector('.nv-cursor-dot');
    dot.innerHTML = '<svg viewBox="0 0 52 44" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8 30 C8 30 10 14 26 12 C42 14 44 30 44 30Z" fill="#F5C015"/><rect x="5" y="29" width="42" height="7" rx="3.5" fill="#0A1540"/><path d="M44 30 Q54 30 50 36 Q45 38 43 36 L43 29Z" fill="#F5C015" stroke="#0A1540" stroke-width="1"/><line x1="15" y1="28" x2="37" y2="28" stroke="#0A1540" stroke-width="1" opacity="0.2"/><text x="26" y="24" text-anchor="middle" fill="#0A1540" font-size="9" font-weight="700" font-family="sans-serif">NL</text></svg>';
    dot.style.width = '40px';
    dot.style.height = '34px';
    const ring = null;
    const label = null;

    document.documentElement.classList.add('nv-cursor-active');

    const state = { x: 0, y: 0, tx: 0, ty: 0 };
    let raf;

    window.addEventListener('mousemove', (e) => {
      state.tx = e.clientX;
      state.ty = e.clientY;
      if (!raf) raf = requestAnimationFrame(loop);
    });

    window.addEventListener('mouseleave', () => { cursor.style.opacity = '0'; });
    window.addEventListener('mouseenter', () => { cursor.style.opacity = '1'; });
    window.addEventListener('mousedown', () => cursor.classList.add('is-down'));
    window.addEventListener('mouseup', () => cursor.classList.remove('is-down'));

    function loop() {
      state.x = lerp(state.x, state.tx, 0.85);
      state.y = lerp(state.y, state.ty, 0.85);
      dot.style.transform = `translate(${state.x}px, ${state.y}px) translate(-50%, -50%)`;
      const dist = Math.hypot(state.tx - state.x, state.ty - state.y);
      if (dist > 0.1) raf = requestAnimationFrame(loop);
      else raf = null;
    }

    // hover detection
    const interactiveSel = 'a, button, [role="button"], input, select, textarea, [data-cursor], [data-magnetic]';
    document.body.addEventListener('mouseover', (e) => {
      if (e.target.closest(interactiveSel)) cursor.classList.add('is-hover');
    });
    document.body.addEventListener('mouseout', (e) => {
      if (e.target.closest(interactiveSel)) cursor.classList.remove('is-hover');
    });
  }

  /* ─── SPLIT TEXT (characters) ─── */
  function initSplit() {
    if (prefersReducedMotion) return;
    document.querySelectorAll('[data-split]').forEach((root) => {
      const spans = root.querySelectorAll('span');
      let charIdx = 0;
      spans.forEach((span) => {
        const text = span.textContent;
        const wrap = document.createElement('span');
        wrap.className = 'nv-split-wrap';
        const frag = document.createDocumentFragment();
        for (const ch of text) {
          const c = document.createElement('span');
          c.className = 'nv-split-char' + (ch === ' ' ? ' space' : '');
          c.style.setProperty('--i', charIdx++);
          c.textContent = ch === ' ' ? '\u00A0' : ch;
          frag.appendChild(c);
        }
        wrap.appendChild(frag);
        // preserve the parent's modifier class (e.g. .accent) by replacing content
        span.textContent = '';
        span.appendChild(wrap);
      });
    });
  }

  /* ─── REVEAL OBSERVER (legacy + new) ─── */
  function initReveal() {
    const sel = '.reveal, .reveal-item, [data-reveal]';
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            en.target.classList.add('visible');
            en.target.classList.add('in-view');
            io.unobserve(en.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll(sel).forEach((el) => {
      // Items inside hero with data-reveal="fade" animate via body.is-loaded, not scroll.
      if (el.closest('.hero') && el.getAttribute('data-reveal') === 'fade') return;
      io.observe(el);
    });
  }

  /* ─── MAGNETIC BUTTONS ─── */
  function initMagnetic() {
    if (!isFinePointer || prefersReducedMotion) return;
    const magnets = document.querySelectorAll('[data-magnetic]');
    const strength = 0.25;
    magnets.forEach((el) => {
      let rect;
      el.addEventListener('mouseenter', () => {
        rect = el.getBoundingClientRect();
      });
      el.addEventListener('mousemove', (e) => {
        if (!rect) rect = el.getBoundingClientRect();
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;
        el.style.transform = `translate(${x * strength}px, ${y * strength}px)`;
      });
      el.addEventListener('mouseleave', () => {
        el.style.transform = '';
        rect = null;
      });
    });
  }

  /* ─── HERO PARALLAX (smoothed mouse + scroll) ─── */
  function initHeroParallax() {
    if (prefersReducedMotion) return;
    const scene = document.querySelector('[data-parallax-scene]');
    if (!scene) return;
    const bg = scene.querySelector('[data-parallax]');

    if (isFinePointer && bg) {
      const target = { x: 0, y: 0 };
      const current = { x: 0, y: 0 };
      let raf = null;
      scene.addEventListener('mousemove', (e) => {
        const rect = scene.getBoundingClientRect();
        target.x = (e.clientX - rect.left) / rect.width - 0.5;
        target.y = (e.clientY - rect.top) / rect.height - 0.5;
        if (!raf) raf = requestAnimationFrame(tick);
      });
      scene.addEventListener('mouseleave', () => {
        target.x = 0; target.y = 0;
        if (!raf) raf = requestAnimationFrame(tick);
      });
      function tick() {
        current.x = lerp(current.x, target.x, 0.08);
        current.y = lerp(current.y, target.y, 0.08);
        bg.style.transform = `scale(1.06) translate(${current.x * -22}px, ${current.y * -22}px)`;
        if (Math.abs(current.x - target.x) > 0.001 || Math.abs(current.y - target.y) > 0.001) {
          raf = requestAnimationFrame(tick);
        } else {
          raf = null;
        }
      }
    }

    // subtle scroll parallax
    const onScroll = () => {
      const rect = scene.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) return;
      const progress = 1 - rect.bottom / (window.innerHeight + rect.height);
      if (bg) bg.style.backgroundPosition = `center calc(50% + ${progress * 40}px)`;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ─── HEADER scroll behavior ─── */
  function initHeader() {
    const header = document.querySelector('.site-header');
    if (!header) return;
    let lastY = window.scrollY;
    let ticking = false;
    const onScroll = () => {
      const y = window.scrollY;
      header.classList.toggle('is-compact', y > 40);
      if (y > lastY && y > 180) header.classList.add('is-hidden');
      else header.classList.remove('is-hidden');
      lastY = y;
      ticking = false;
    };
    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          requestAnimationFrame(onScroll);
          ticking = true;
        }
      },
      { passive: true }
    );
  }

  /* ─── KINETIC BANNER (scroll-velocity, bidirectional) ─── */
  function initKinetic() {
    if (prefersReducedMotion) return;
    const rows = document.querySelectorAll('[data-kinetic-row]');
    if (!rows.length) return;

    const state = {
      scrollY: window.scrollY,
      velocity: 0,
      boost: 0,
      offsets: new Map(),
    };
    rows.forEach((row) => state.offsets.set(row, 0));

    window.addEventListener('scroll', () => {
      const y = window.scrollY;
      state.velocity = y - state.scrollY;
      state.scrollY = y;
      state.boost = clamp(state.boost + state.velocity * 0.6, -60, 60);
    }, { passive: true });

    const base = 0.8;
    let kRaf = null;
    function tick() {
      state.boost = lerp(state.boost, 0, 0.08);
      rows.forEach((row) => {
        const dir = parseFloat(row.getAttribute('data-kinetic-row')) || 1;
        const track = row.querySelector('.kinetic-track');
        if (!track) return;
        let off = state.offsets.get(row) || 0;
        off += (base + Math.abs(state.boost) * 0.15) * dir;
        if (dir > 0 && off < -track.scrollWidth / 2) off += track.scrollWidth / 2;
        if (dir < 0 && off > 0) off -= track.scrollWidth / 2;
        state.offsets.set(row, off);
        track.style.transform = `translate3d(${off}px, 0, 0)`;
      });
      kRaf = requestAnimationFrame(tick);
    }
    kRaf = requestAnimationFrame(tick);
  }

  /* ─── MARQUEE velocity boost ─── */
  function initMarqueeVelocity() {
    if (prefersReducedMotion) return;
    const track = document.querySelector('.marquee-track');
    if (!track) return;
    let lastY = window.scrollY;
    let boost = 0;
    let mRaf = null;
    function tick() {
      boost = lerp(boost, 0, 0.06);
      const duration = Math.max(6, 25 - Math.abs(boost));
      track.style.animationDuration = duration + 's';
      if (Math.abs(boost) > 0.05) mRaf = requestAnimationFrame(tick);
      else mRaf = null;
    }
    window.addEventListener('scroll', () => {
      const y = window.scrollY;
      const v = y - lastY;
      lastY = y;
      boost = clamp(boost + v * 0.2, -20, 20);
      if (!mRaf) mRaf = requestAnimationFrame(tick);
    }, { passive: true });
  }

  /* ─── 3D TILT on product cards ─── */
  function initTilt() {
    if (!isFinePointer || prefersReducedMotion) return;
    const cards = document.querySelectorAll('.product-card');
    const max = 8;
    cards.forEach((card) => {
      let rect;
      const onEnter = () => (rect = card.getBoundingClientRect());
      const onMove = (e) => {
        if (!rect) rect = card.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width - 0.5;
        const y = (e.clientY - rect.top) / rect.height - 0.5;
        card.style.transform = `perspective(1200px) rotateX(${-y * max}deg) rotateY(${x * max}deg) translateZ(0)`;
      };
      const onLeave = () => {
        card.style.transform = '';
        rect = null;
      };
      card.addEventListener('mouseenter', onEnter);
      card.addEventListener('mousemove', onMove);
      card.addEventListener('mouseleave', onLeave);

      // inject a quick-view label inside product-image for the hover overlay
      const img = card.querySelector('.product-image');
      const imgLink = card.querySelector('.product-image-link');
      if (img && !img.querySelector('.product-quick')) {
        const qv = document.createElement('a');
        qv.className = 'product-quick';
        qv.textContent = 'View Product';
        if (imgLink) qv.href = imgLink.href;
        img.appendChild(qv);
      }
    });
  }

  /* ─── COPY discount code on click ─── */
  function initCopyCode() {
    const code = document.querySelector('.discount-code');
    if (!code) return;
    code.setAttribute('role', 'button');
    code.setAttribute('tabindex', '0');
    const doCopy = async () => {
      const text = code.textContent.trim();
      try {
        await navigator.clipboard.writeText(text);
        const old = code.textContent;
        code.textContent = 'Copied ✓';
        setTimeout(() => (code.textContent = old), 1400);
      } catch (_) {}
    };
    code.addEventListener('click', doCopy);
    code.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        doCopy();
      }
    });
  }

  /* ─── BOOT ─── */
  function boot() {
    initLoader();
    initSplit();
    initReveal();
    initProgress();
    initCursor();
    initMagnetic();
    initHeroParallax();
    initHeader();
    initKinetic();
    initMarqueeVelocity();
    initTilt();
    initCopyCode();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
